import { DEFAULT_SOURCE_CHAIN_ID, chainLabel } from "../config/chains";
import { useMemo, useState } from "react";
import { readOptionalPreference, writeOptionalPreference } from "../state/optional-preferences";
import { calculateMintFeeProgress, formatEth } from "../lib/financial";
import type { EffectsLevel, MiningMode, MiningPowerMode, MintReviewView, NftView, TerminalAppModel } from "../state/ui-types";

const sourceAddress = "0xCcAEDCF03cF79a914454E78c89104bFCE6Cf8E01";
const collectionAddress = "0x31b1939e53385631B0B27cDA97840995319ABf20";
const walletAddress = "0x7A4fD9149E3B0B4d7cCd41aE61C7cFa8b83A291C";

type DemoState = "disconnected" | "mining" | "verified" | "pending" | "finalized" | "blocked" | "fee299" | "fee300" | "redeem" |
  "loading" | "empty" | "stale" | "expired" | "large" | "disconnected-failure" |
  "quote" | "quote-expired" | "rejected" | "uncertain" | "action-error" | "ready" | "redeemed" | "media-unavailable" | "tiers";

export function useDemoTerminalModel(): TerminalAppModel {
  const initial = demoStateFromUrl();
  const [connected, setConnected] = useState(initial !== "disconnected" && initial !== "disconnected-failure");
  const [walletSelectionOpen, setWalletSelectionOpen] = useState(false);
  const [phase, setPhase] = useState<TerminalAppModel["mining"]["phase"]>(
    initial === "mining" ? "MINING" : ["verified", "quote", "quote-expired"].includes(initial) ? "VERIFIED" : "READY",
  );
  const [mode, setMode] = useState<MiningMode>("GPU");
  const [powerMode, setPowerMode] = useState<MiningPowerMode>(1);
  const [effects, setEffectsState] = useState<EffectsLevel>(() => {
    const saved = readOptionalPreference("greenwood:effects");
    return saved === "ON" || saved === "LOW" || saved === "OFF" ? saved : "ON";
  });
  const [mintCount, setMintCount] = useState(() => initial === "fee299" ? "299" : initial === "fee300" ? "300" : "1284");
  const [items, setItems] = useState<NftView[]>(() => demoNfts(initial));
  const blocked = initial === "blocked" || initial === "disconnected-failure";
  const resourceFailure = initial === "disconnected-failure";
  const loading = initial === "loading";
  // Visual-only review bindings; demo actions never ask a wallet to send them.
  const [mintReview] = useState<MintReviewView | null>(() => initial === "quote" || initial === "quote-expired" ? {
    id: "demo-review", contextKey: "demo:true", proofId: `0x${"88".repeat(32)}`, tierUsd: 100,
    account: walletAddress, sourceToken: sourceAddress, chainId: DEFAULT_SOURCE_CHAIN_ID, collection: collectionAddress,
    feeWei: "10000000000000001", deadline: Math.floor(Date.now() / 1_000) + (initial === "quote" ? 600 : -60),
  } : null);

  return useMemo<TerminalAppModel>(() => {
    const feeProgress = calculateMintFeeProgress(BigInt(mintCount));
    return ({
    contextKey: `demo:${connected}`,
    walletSelection: { isOpen: walletSelectionOpen, isDetecting: false, pendingId: null, error: null,
      wallets: [{ id: "demo", name: "Forest demo wallet", icon: null, kind: "extension" }] },
    brandName: import.meta.env.VITE_BRAND_NAME || "MERKLEWOOD",
    environment: "DEMO",
    environmentLabel: "DEMO",
    environmentNote: "Explicit visual fixture data. No request or transaction is sendable.",
    walletTransactionsEnabled: true,
    mintReview,
    transactions: { items: initial === "rejected" || initial === "uncertain" ? [{
      id: "demo-transaction", kind: "MINT", account: walletAddress, chainId: DEFAULT_SOURCE_CHAIN_ID,
      collection: collectionAddress, sourceToken: sourceAddress, environment: "DEMO", tokenId: null,
      hash: initial === "uncertain" ? `0x${"31".repeat(32)}` : null,
      phase: initial === "uncertain" ? "UNCONFIRMED" : "REJECTED", createdAt: 1_790_505_027_000,
      message: initial === "uncertain" ? "Demo receipt remains uncertain. Check confirmation; do not resend." :
        "Demo wallet request rejected. No transaction hash was returned.",
    }] : [], storageWarning: null, blocked: initial === "uncertain" },
    sourceTokens: [{ address: sourceAddress, symbol: "ARBOR", name: "Arbor Test Token", chainId: DEFAULT_SOURCE_CHAIN_ID, active: true }],
    activeSourceToken: { address: sourceAddress, symbol: "ARBOR", name: "Arbor Test Token", chainId: DEFAULT_SOURCE_CHAIN_ID, active: true },
    wallet: {
      signInConfiguration: "READY",
      status: connected ? initial === "expired" ? "SESSION_EXPIRED" : "CONNECTED" : "DISCONNECTED",
      address: connected ? walletAddress : null,
      chainId: connected ? DEFAULT_SOURCE_CHAIN_ID : null,
      chainLabel: chainLabel(DEFAULT_SOURCE_CHAIN_ID),
      sessionExpiresAt: connected ? Math.floor(Date.now() / 1_000) + 3_600 : null,
      error: null,
    },
    netBuy: {
      tierDisplay: {
        status: blocked ? "UNAVAILABLE" : "CURRENT",
        eligibleTiers: blocked ? null : [10, 100, 500, 1_000], maxTierUsd: blocked ? null : 1_000,
        asOfBlock: 72_086_763, asOfBlockHash: `0x${"a4".repeat(32)}`, authorization: false, updating: false,
      },
      loading,
      netBuyEth: "0.4382",
      netBuyUsd: blocked ? null : "1095.50000000",
      netBuyScope: "CONFIRMED_SNAPSHOT",
      eligibilityAvailable: !blocked,
      eligibleTiers: blocked ? null : [10, 100, 500, 1_000],
      maxTierUsd: blocked ? null : 1_000,
      blockingReasons: resourceFailure ? ["BACKEND_UNAVAILABLE"] : blocked ? ["SCANNER_NOT_CAUGHT_UP", "ETH_USD_PRICE_STALE"] : [],
      asOfBlock: 72_086_763,
      asOfBlockHash: `0x${"a4".repeat(32)}`,
      priceSource: "DEMO_FIXTURE",
      priceTimestamp: 1_790_505_027,
    },
    rewardPool: {
      loading: false,
      available: !blocked,
      availableWei: blocked ? null : "10482000000000000000",
      availableEth: blocked ? null : "10.482",
      totalPaidEth: null,
      vaultAddress: "0xEa5D37431BA9b131c9cC16D10a76CE057d5FdC73",
      asOfBlock: blocked ? null : 72_086_763,
      blockingReasons: resourceFailure ? ["BACKEND_UNAVAILABLE"] : blocked ? ["INDEXER_STALE"] : [],
    },
    mintFee: {
      loading: false,
      available: true,
      powTotalMinted: null, powTotalMintedAsOfBlock: null, cumulativeTotalMinted: mintCount,
      currentFeeWei: feeProgress.currentFeeWei.toString(),
      nextStepFeeWei: feeProgress.nextStepFeeWei.toString(),
      currentFeeEth: formatEth(feeProgress.currentFeeWei, 6),
      collectionAddress,
      blockNumber: 72_086_763,
      mintEnabled: false,
      testOnly: true,
      reason: "DEMO_MODE_NOT_SENDABLE",
    },
    rewardPolicy: {
      loading: false,
      available: true,
      policyVersion: "1",
      policyHash: "0x6643f984594f0bb73e5e2a36d03f580a6ede9942383be2b139c9057a4ae8bfa2",
      tiers: [
        policyTier(10, 10, 50), policyTier(100, 100, 300), policyTier(500, 300, 500),
        policyTier(1_000, 500, 1_000), policyTier(2_000, 1_000, 1_500), policyTier(5_000, 1_500, 2_000),
      ],
      blockingReason: null,
    },
    mining: {
      phase,
      mode,
      powerMode,
      gpuAvailable: true,
      gpuReason: null,
      measuredHashrate: phase === "MINING" ? "812.4 kH/s" : null,
      attempts: phase === "MINING" ? "1,284,901" : phase === "VERIFIED" ? "4,801,221" : "0",
      activeElapsedMs: phase === "MINING" ? 8 * 60_000 + 42_000 : phase === "VERIFIED" ? 33 * 60_000 + 9_000 : 0,
      difficultyLabel: "16 bits · DEMO",
      challengeId: `0x${"44".repeat(32)}`,
      challengeExpiresAt: Math.floor(Date.now() / 1_000) + 38_412,
      proofId: phase === "VERIFIED" ? `0x${"88".repeat(32)}` : null,
      statusMessage: phase === "VERIFIED" ? "Proof accepted by backend" : "Ready for explicit user start",
      canRetrySubmission: false,
      demoOnly: true,
      logs: phase === "MINING" ? [
        { id: "1", at: Date.now() - 8_000, level: "INFO", message: "Visual demo worker state started" },
        { id: "2", at: Date.now() - 3_000, level: "WARNING", message: "Numbers are fixture data, not measured hardware" },
      ] : phase === "VERIFIED" ? [
        { id: "1", at: Date.now() - 1_000, level: "SUCCESS", message: "Demo proof fixture accepted" },
      ] : [],
    },
    inventory: {
      loading,
      loadingMore: false,
      available: !blocked,
      items: blocked || loading ? null : items,
      hasMore: false,
      blockingReasons: resourceFailure ? ["BACKEND_UNAVAILABLE"] : blocked ? ["NFT_INDEXER_STALE"] : [],
      updateError: initial === "stale" ? "Demo refresh failure" : null,
      freshnessLabel: loading ? "Loading canonical inventory" : blocked ? "Canonical ownership is temporarily unavailable" : "Confirmed ownership · demo fixture",
    },
    runtime: {
      backend: resourceFailure ? "OFFLINE" : "DEMO",
      price: blocked ? "STALE" : "LIVE",
      tradeCoverage: blocked ? "RECOVERING" : "COMPLETE",
      randomness: "LOCAL_TEST",
      publicMiningEnabled: false,
      publicMintEnabled: false,
      updatedAt: Date.now(),
    },
    effects,
    setEffects(level) { setEffectsState(level); writeOptionalPreference("greenwood:effects", level); },
    actions: {
      openWalletSelection() { setWalletSelectionOpen(true); },
      closeWalletSelection() { setWalletSelectionOpen(false); },
      connectWallet() { setWalletSelectionOpen(false); setConnected(true); },
      connectWalletOnly() { setWalletSelectionOpen(false); setConnected(true); },
      disconnectWallet() { setConnected(false); if (phase === "MINING") setPhase("PAUSED"); },
      switchNetwork() {},
      selectSourceToken() {},
      setMiningMode(next) { setMode(next); },
      setMiningPowerMode(next) { setPowerMode(next); },
      startMining() { setPhase("MINING"); },
      pauseMining() { setPhase("PAUSED"); },
      resumeMining() { setPhase("MINING"); },
      retryMiningSubmission() {},
      checkTransaction() {},
      dismissTransaction() {},
      stopMining() { setPhase("READY"); },
      refresh() { if (initial === "action-error") throw new Error("Demo refresh failed. Try again."); },
      loadMoreNfts() {},
      refreshInventory() {},
      requestRedeemPreview(tokenId) {
        // Explicit visual fixture only: expose a redeemed detail after selection.
        if (initial === "redeemed") setItems(current => current.map(nft => nft.tokenId === tokenId ?
          { ...nft, status: "BURNED", owner: null, redeemable: false, imageUrl: null, imageKind: "UNAVAILABLE",
            randomnessStatus: "BURNED", expectedPayoutEth: null, expectedPayoutWei: null } : nft));
      },
      prepareMint(tierUsd) {
        setPhase("READY");
        setItems((current) => [{ ...pendingNft, tierUsd, tokenId: String(BigInt(mintCount) + 1n) }, ...current]);
        setMintCount((count) => (BigInt(count) + 1n).toString());
      },
      redeemNft(tokenId) { setItems((current) => current.filter((nft) => nft.tokenId !== tokenId)); },
    },
    });
  }, [blocked, connected, effects, initial, items, loading, mintCount, mintReview, mode, phase, powerMode, resourceFailure, walletSelectionOpen]);
}

function policyTier(tierUsd: number, minRate: number, maxRate: number) {
  const widths = Math.floor((maxRate - minRate + 1) / 5);
  const weights = [6_000, 2_500, 1_000, 400, 100];
  return {
    tierUsd, minRate, maxRate, rewardDenominator: 10_000,
    bands: weights.map((weightBps, index) => ({
      minRate: minRate + widths * index,
      maxRate: index === 4 ? maxRate : minRate + widths * (index + 1) - 1,
      weightBps,
    })),
  };
}

function demoStateFromUrl(): DemoState {
  const value = new URLSearchParams(window.location.search).get("state");
  return ["disconnected", "mining", "verified", "pending", "finalized", "blocked", "fee299", "fee300", "redeem",
    "loading", "empty", "stale", "expired", "large", "disconnected-failure", "quote", "quote-expired", "rejected", "uncertain", "action-error", "ready", "redeemed", "media-unavailable", "tiers"].includes(value ?? "")
    ? value as DemoState : "finalized";
}

const pendingNft: NftView = {
  tokenId: "1285", collectionAddress, owner: walletAddress, sourceToken: sourceAddress, sourceSymbol: "ARBOR",
  tierUsd: 1_000, status: "PENDING_RANDOMNESS", rewardBps: null, rewardBand: null, imageUrl: null,
  imageKind: "PENDING", mintTxHash: `0x${"31".repeat(32)}`, finalizeTxHash: null, snapshotBlock: 72_086_764,
  randomnessStatus: "PENDING_PROVIDER_REVEAL", redeemable: false, expectedPayoutWei: null, expectedPayoutEth: null,
};

function demoNfts(state: DemoState): NftView[] {
  if (state === "empty") return [];
  if (state === "tiers") return [10, 100, 500, 1_000, 2_000, 5_000].map((tierUsd, index) =>
    ({ ...pendingNft, tokenId: String(1285 + index), tierUsd }));
  if (state === "large") return Array.from({ length: 1000 }, (_, index) => makeFinalNft(String(1000 + index), 1000, 742, 4, "0.777762"));
  const finalized: NftView[] = [
    makeFinalNft("1284", 1_000, 742, 4, "0.777762"),
    makeFinalNft("1279", 500, 418, 2, "0.438152"),
    makeFinalNft("1242", 100, 184, 1, "0.192873"),
  ];
  if (state === "pending") return [pendingNft, ...finalized];
  if (state === "ready") return [{ ...pendingNft, status: "RANDOMNESS_READY", randomnessStatus: "RANDOMNESS_READY" }, ...finalized];
  if (state === "media-unavailable") return [{ ...pendingNft, imageKind: "UNAVAILABLE" }, { ...finalized[0]!, imageUrl: null, imageKind: "UNAVAILABLE" }];
  if (state === "redeem") return [finalized[0]!];
  return finalized;
}

function makeFinalNft(tokenId: string, tierUsd: number, rewardBps: number, rewardBand: number, payout: string): NftView {
  return {
    tokenId, collectionAddress, owner: walletAddress, sourceToken: sourceAddress, sourceSymbol: "ARBOR", tierUsd,
    status: "FINALIZED", rewardBps, rewardBand, imageUrl: demoNftImage(tokenId, tierUsd, rewardBps),
    imageKind: "DEMO_PREVIEW", mintTxHash: `0x${tokenId.padStart(64, "0")}`, finalizeTxHash: `0x${"42".repeat(32)}`,
    snapshotBlock: 72_086_763, randomnessStatus: "FINALIZED", redeemable: true,
    expectedPayoutWei: "777762000000000000", expectedPayoutEth: payout,
  };
}

function demoNftImage(tokenId: string, tierUsd: number, rewardBps: number): string {
  const percent = `${Math.floor(rewardBps / 100)}.${String(rewardBps % 100).padStart(2, "0")}%`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600"><defs><linearGradient id="g" x2="1" y2="1"><stop stop-color="#07110c"/><stop offset="1" stop-color="#284c2b"/></linearGradient><radialGradient id="r"><stop stop-color="#d5af65" stop-opacity=".85"/><stop offset="1" stop-color="#79d657" stop-opacity="0"/></radialGradient></defs><rect width="600" height="600" fill="url(#g)"/><circle cx="300" cy="250" r="210" fill="url(#r)" opacity=".45"/><path d="M300 74 472 224l-56 220H184l-56-220Z" fill="#101e16" stroke="#d5af65" stroke-width="10"/><path d="m300 120 92 132-52-9 62 105H198l62-105-52 9Z" fill="#79d657" opacity=".88"/><path d="M300 170 246 334h108Z" fill="#07110c"/><text x="300" y="468" text-anchor="middle" fill="#e8ead9" font-family="monospace" font-size="34">${percent}</text><text x="300" y="515" text-anchor="middle" fill="#d5af65" font-family="monospace" font-size="18">TIER $${tierUsd} · #${tokenId}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
